import React, { useEffect, useState } from "react";
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
} from "@mui/material";
import { PersonOutline } from "@mui/icons-material";
import whatsapp from "../../../../components/icons/whatsapp.png";
import Trash from "../../../../components/icons/trash.png";
import sms from "../../../../components/icons/sms.png";
import call from "../../../../components/icons/call.png";
import PenPaperIcon from "../../../../components/icons/PenPaperIcon";
import { IContactData, VenueData } from "../../types/venues.types";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import { User } from "../../../../store/slice/AuthSlice";
import apiService from "../../../../service/Api";
import { toast } from "react-toastify";
import DialogEditVenueIndividualContacts from "../../view-venue/components/DialogEditVenueIndividualContacts";

interface CompactContactCardProps {
  contact: IContactData;
  venueData: VenueData | null;
  onEdit?: (updatedContact: IContactData) => void;
  onRemove?: (contactId: number) => void;
  variant?: "sidebar" | "tab";
}

const CompactContactCard: React.FC<CompactContactCardProps> = ({
  contact,
  onEdit,
  onRemove,
  venueData,
  variant = "sidebar",
}) => {
  const user = useSelector(
    (state: RootState) => state.auth.user,
  ) as User | null;
  const [isEditing, setIsEditing] = useState(false);
  const [currentContact, setCurrentContact] = useState<IContactData>(contact);

  // Use this boolean for all conditional logic
  const isTabVariant = variant === "tab";

  useEffect(() => {
    setCurrentContact(contact);
  }, [contact]);

  const toTitleCase = (str?: string | null) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handleEditComplete = (updatedContact: IContactData) => {
    setIsEditing(false);
    setCurrentContact(updatedContact);
    if (onEdit) onEdit(updatedContact);
  };

  const handleRemove = async () => {
    if (!currentContact || !venueData) return;
    try {
      await apiService.patch(
        `/api/venues/venue/${venueData.id}/remove-contact/`,
        { contact_id: currentContact.id },
      );
      toast.success("Contact removed from venue");
      if (onRemove) onRemove(currentContact.id);
    } catch (error) {
      console.error(error);
      toast.error("Failed to remove contact from venue");
    }
  };

  return (
    <>
      <Box
        sx={{
          height: isTabVariant ? 210 : 180,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          boxSizing: "border-box",
          transition: "height 0.2s ease-in-out",
        }}
      >
        {/* Top row: delete + edit icons */}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
        >
          <IconButton
            size="small"
            sx={{ color: "error.main" }}
            onClick={handleRemove}
          >
            <Box component="img" src={Trash} sx={{ width: 18, height: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={() => setIsEditing(true)}>
            <PenPaperIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* Avatar */}
        <Avatar
          src={currentContact.contact_image || undefined}
          sx={{ width: 48, height: 48, bgcolor: "primary.main" }}
        >
          {!currentContact.contact_image && (
            <PersonOutline sx={{ fontSize: 24 }} />
          )}
        </Avatar>

        {/* Name + Role + contact info */}
        <Box sx={{ textAlign: "center", lineHeight: 1.2, width: "100%" }}>
          <Typography variant="subtitle2" fontWeight={600} fontSize={13}>
            {toTitleCase(currentContact.first_name)}{" "}
            {toTitleCase(currentContact.last_name)}
          </Typography>

          {/* Conditional Role Chip */}
          {isTabVariant && (
            <Chip
              label={currentContact.role}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                bgcolor: "grey.200",
                color: "text.primary",
                mt: 0.5,
                mb: 0.5,
                fontWeight: 600,
              }}
            />
          )}

          {currentContact.email && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              fontSize={11}
            >
              {currentContact.email}
            </Typography>
          )}
          {currentContact.phone && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              fontSize={11}
            >
              {currentContact.phone}
            </Typography>
          )}
        </Box>

        {/* Action icons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {currentContact.phone && (
            <IconButton
              size="small"
              onClick={() => {
                const phone = (currentContact.phone || "").replace(/\D/g, "");
                const firstName = currentContact.first_name
                  ? toTitleCase(currentContact.first_name)
                  : "there";
                const message = `Dear ${firstName},\nThis is ${user?.first_name} ${user?.last_name} from Muzukuru Funeral Streaming.\nI am reaching out about ${venueData?.name}`;
                window.open(
                  `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
                  "_blank",
                );
              }}
            >
              <Box
                component="img"
                src={whatsapp}
                sx={{ width: 17, height: 17 }}
              />
            </IconButton>
          )}
          {currentContact.email && (
            <IconButton
              size="small"
              onClick={() => window.open(`mailto:${currentContact.email}`)}
            >
              <Box component="img" src={sms} sx={{ width: 17, height: 17 }} />
            </IconButton>
          )}
          {currentContact.phone && (
            <IconButton
              size="small"
              onClick={() => window.open(`tel:${currentContact.phone}`)}
            >
              <Box component="img" src={call} sx={{ width: 17, height: 17 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Edit dialog */}
      <Dialog
        open={isEditing}
        onClose={() => setIsEditing(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Contact</DialogTitle>
        <DialogContent>
          <DialogEditVenueIndividualContacts
            contact={currentContact}
            onComplete={handleEditComplete}
            onCancel={() => setIsEditing(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompactContactCard;
